import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import Button from "@mui/material/Button";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import {
  getVatDraft,
  recordVatCorrection,
  saveManualVatDraft,
  submitManualVatDraft,
} from "services/tenantUserAdminService";

const EDITABLE_BOXES = ["box1", "box2", "box4", "box6", "box7", "box8", "box9"];
const ALL_BOXES = ["box1", "box2", "box3", "box4", "box5", "box6", "box7", "box8", "box9"];

function roundAmount(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function recalculateBoxes(nextBoxes) {
  const box1 = roundAmount(nextBoxes.box1);
  const box2 = roundAmount(nextBoxes.box2);
  const box4 = roundAmount(nextBoxes.box4);
  const box3 = roundAmount(box1 + box2);
  const box5 = roundAmount(box3 - box4);

  return {
    ...nextBoxes,
    box1: `${box1}`,
    box2: `${box2}`,
    box3: `${box3}`,
    box4: `${box4}`,
    box5: `${box5}`,
    box6: `${roundAmount(nextBoxes.box6)}`,
    box7: `${roundAmount(nextBoxes.box7)}`,
    box8: `${roundAmount(nextBoxes.box8)}`,
    box9: `${roundAmount(nextBoxes.box9)}`,
  };
}

function emptyBoxes() {
  return recalculateBoxes({
    box1: "0",
    box2: "0",
    box3: "0",
    box4: "0",
    box5: "0",
    box6: "0",
    box7: "0",
    box8: "0",
    box9: "0",
  });
}

function boxLabel(box) {
  const labels = {
    box1: "VAT due on sales and other outputs",
    box2: "VAT due on acquisitions from other EC member states",
    box3: "Total VAT due",
    box4: "VAT reclaimed in this period",
    box5: "Net VAT to pay or reclaim",
    box6: "Total value of sales excluding VAT",
    box7: "Total value of purchases excluding VAT",
    box8: "Total value of supplies to other EC member states",
    box9: "Total value of acquisitions from other EC member states",
  };
  return labels[box] || box.toUpperCase();
}

function VATSubmissionForm({ tenantId: propTenantId, clientId: propClientId, period: propPeriod }) {
  const [boxes, setBoxes] = useState(emptyBoxes);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [finalised, setFinalised] = useState(false);
  const [notes, setNotes] = useState("");
  const [originalSuggestions, setOriginalSuggestions] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const resultRef = useRef(null);

  const params = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  const tenantId = propTenantId || params.tenantId || qs.get("tenantId");
  const clientId = propClientId || params.clientId || qs.get("clientId");
  const period = propPeriod || qs.get("period") || params.period || "";

  useEffect(() => {
    async function loadDraft() {
      if (!tenantId || !clientId || !period) return;
      setLoading(true);
      setError(null);
      try {
        const draft = await getVatDraft(tenantId, clientId, period);
        const loadedBoxes = draft?.boxes || {};
        setBoxes(recalculateBoxes({
          box1: loadedBoxes.box1 ?? "0",
          box2: loadedBoxes.box2 ?? "0",
          box3: loadedBoxes.box3 ?? "0",
          box4: loadedBoxes.box4 ?? "0",
          box5: loadedBoxes.box5 ?? "0",
          box6: loadedBoxes.box6 ?? "0",
          box7: loadedBoxes.box7 ?? "0",
          box8: loadedBoxes.box8 ?? "0",
          box9: loadedBoxes.box9 ?? "0",
        }));
        setStatus(draft?.status || "draft");
        setFinalised(Boolean(draft?.finalised || draft?.declarationConfirmed));
        setNotes(draft?.notes || "");

        try {
          const suggestions = draft?.aiSuggestions?.suggestions || draft?.aiSuggestions || [];
          const map = {};
          if (Array.isArray(suggestions)) {
            suggestions.forEach((item) => {
              if (item.box && item.amount !== undefined) map[item.box] = `${item.amount}`;
            });
          } else if (typeof suggestions === "object" && suggestions) {
            Object.keys(suggestions).forEach((key) => {
              map[key] = `${suggestions[key]}`;
            });
          }
          setOriginalSuggestions(map);
        } catch {
          setOriginalSuggestions({});
        }
      } catch (e) {
        setError(e.message || "Failed to load VAT draft");
      } finally {
        setLoading(false);
      }
    }

    loadDraft();
  }, [tenantId, clientId, period]);

  function validateBoxes({ requireFinalised = false } = {}) {
    if (!tenantId || !clientId || !period) {
      setError("Tenant, client, and VAT period are required.");
      return null;
    }

    for (const box of EDITABLE_BOXES) {
      const value = `${boxes[box]}`.trim();
      if (value === "") {
        setError("Complete all required VAT boxes before saving.");
        return null;
      }
      if (!Number.isFinite(Number(value))) {
        setError(`VAT ${box.toUpperCase()} must be a valid number.`);
        return null;
      }
    }

    if (requireFinalised && !finalised) {
      setError("Confirm that the VAT return is finalised before submitting.");
      return null;
    }

    setError(null);
    return {
      box1: roundAmount(boxes.box1),
      box2: roundAmount(boxes.box2),
      box3: roundAmount(boxes.box3),
      box4: roundAmount(boxes.box4),
      box5: roundAmount(boxes.box5),
      box6: roundAmount(boxes.box6),
      box7: roundAmount(boxes.box7),
      box8: roundAmount(boxes.box8),
      box9: roundAmount(boxes.box9),
    };
  }

  async function persistManualDraft({ requireFinalised = false } = {}) {
    const payloadBoxes = validateBoxes({ requireFinalised });
    if (!payloadBoxes) return null;

    return saveManualVatDraft(tenantId, clientId, period, {
      boxes: payloadBoxes,
      finalised,
      notes,
    });
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!ALL_BOXES.includes(name) || name === "box3" || name === "box5") return;
    setBoxes((prev) => recalculateBoxes({ ...prev, [name]: value }));
  };

  const handleBlur = async (box) => {
    try {
      const original = originalSuggestions[box];
      const current = `${boxes[box]}`;
      if (original !== undefined && original !== null && original !== "" && original !== current) {
        await recordVatCorrection(tenantId, clientId, period, {
          box,
          suggested: original,
          corrected: current,
          reason: "user_override",
        }).catch(() => {});
        setOriginalSuggestions((prev) => ({ ...prev, [box]: current }));
      }
    } catch {
      // Ignore feedback capture failures in manual mode.
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const draft = await persistManualDraft();
      if (!draft) return;
      setStatus(draft?.status || "draft");
      setResult(null);
    } catch (e) {
      setError(e.message || "Failed to save manual VAT draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (validateBoxes({ requireFinalised: true })) {
      setConfirmOpen(true);
    }
  };

  const handlePreview = () => {
    if (validateBoxes()) {
      setPreviewOpen(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const draft = await persistManualDraft({ requireFinalised: true });
      if (!draft) return;

      const res = await submitManualVatDraft(tenantId, clientId, period);
      setStatus(res?.status || "submitted");
      setResult({
        submissionId: res?.receipt?.submissionId || res?.submission_id || `MANUAL-${Date.now()}`,
        timestamp: res?.submitted_at || new Date().toISOString(),
        status: "Submitted manually",
        receipt: res?.receipt?.message || "Recorded in LedgerHub only. Not sent to HMRC.",
      });
      setError(null);
      setTimeout(() => resultRef.current?.focus(), 100);
    } catch (e) {
      setError(e.message || "Manual submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VuiBox role="form" aria-label="VAT return submission form">
      {loading ? (
        <VuiBox display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </VuiBox>
      ) : (
        <>
          <VuiBox
            mb={2}
            p={2}
            sx={{
              borderRadius: "12px",
              background: "rgba(41, 98, 255, 0.06)",
              border: "1px solid rgba(41, 98, 255, 0.18)",
            }}
          >
            <VuiTypography color="info" fontWeight="bold" mb={0.75}>
              Manual VAT entry mode
            </VuiTypography>
            <VuiTypography color="text" id="vat-form-instructions">
              Enter the VAT return values for period <strong>{period || "—"}</strong>, save the draft, then
              mark it as manually submitted. This records the return in LedgerHub and does not send anything to HMRC.
            </VuiTypography>
          </VuiBox>

          <VuiBox display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} aria-describedby="vat-form-instructions" sx={{ position: "relative" }}>
            {ALL_BOXES.map((box) => {
              const derived = box === "box3" || box === "box5";
              return (
                <TextField
                  key={box}
                  name={box}
                  label={box.toUpperCase()}
                  value={boxes[box]}
                  onChange={handleChange}
                  onBlur={() => handleBlur(box)}
                  required={!derived}
                  disabled={status === "submitted" || submitting || derived}
                  helperText={derived ? "Calculated automatically" : ""}
                  inputProps={{
                    "aria-label": `VAT ${box.toUpperCase()} amount`,
                    inputMode: "decimal",
                    style: { pointerEvents: "auto" },
                  }}
                  InputProps={{ style: { position: "relative", zIndex: 2 } }}
                />
              );
            })}
          </VuiBox>

          <VuiBox mt={2} display="grid" gap={2}>
            <TextField
              label="Internal notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={3}
              disabled={status === "submitted" || submitting}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={finalised}
                  onChange={(e) => setFinalised(e.target.checked)}
                  disabled={status === "submitted" || submitting}
                />
              }
              label="I confirm this VAT return is finalised and ready to be recorded as submitted."
            />
          </VuiBox>

          <VuiBox
            mt={2}
            p={2}
            sx={{
              borderRadius: "12px",
              background: "rgba(15, 23, 42, 0.03)",
              border: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <VuiTypography fontWeight="bold" mb={1}>
              Preview summary
            </VuiTypography>
            <VuiBox display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1.25}>
              <VuiTypography variant="caption">Period</VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold">{period || "—"}</VuiTypography>
              <VuiTypography variant="caption">Submission method</VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold">Manual record only</VuiTypography>
              <VuiTypography variant="caption">Finalised</VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold">{finalised ? "Yes" : "No"}</VuiTypography>
              <VuiTypography variant="caption">Box 3</VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold">{boxes.box3}</VuiTypography>
              <VuiTypography variant="caption">Box 5</VuiTypography>
              <VuiTypography variant="caption" fontWeight="bold">{boxes.box5}</VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiBox mt={2} display="flex" gap={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSave}
              disabled={saving || submitting || status === "submitted"}
              aria-label="Save manual VAT draft"
            >
              {saving ? "Saving…" : "Save Manual Draft"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handlePreview}
              disabled={saving || submitting}
              aria-label="Preview manual VAT return"
            >
              Preview Return
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving || submitting || status === "submitted"}
              aria-label="Record manual VAT submission"
            >
              {submitting ? "Submitting…" : "Submit Manually"}
            </Button>
          </VuiBox>

          <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} aria-labelledby="preview-vat-title" maxWidth="md" fullWidth>
            <DialogTitle id="preview-vat-title">Preview manual VAT return</DialogTitle>
            <DialogContent dividers>
              <VuiTypography mb={2}>
                Review the VAT return below before final manual submission for period {period}.
              </VuiTypography>
              <VuiBox display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 120px)" gap={1.25}>
                {ALL_BOXES.map((box) => (
                  <React.Fragment key={box}>
                    <VuiTypography>{box.toUpperCase()} — {boxLabel(box)}</VuiTypography>
                    <VuiTypography fontWeight="bold" textAlign="right">{boxes[box]}</VuiTypography>
                  </React.Fragment>
                ))}
              </VuiBox>
              {notes && (
                <VuiBox mt={2}>
                  <VuiTypography fontWeight="bold" mb={0.5}>Internal notes</VuiTypography>
                  <VuiTypography>{notes}</VuiTypography>
                </VuiBox>
              )}
              <VuiTypography mt={2} color={finalised ? "success" : "warning"}>
                {finalised
                  ? "This return has been marked as finalised."
                  : "This return is not yet marked as finalised. You will need to confirm finalisation before submitting."}
              </VuiTypography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewOpen(false)}>Close</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setPreviewOpen(false);
                  handleSubmit();
                }}
                disabled={saving || submitting}
              >
                Continue to submit
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} aria-labelledby="confirm-submit-title">
            <DialogTitle id="confirm-submit-title">Confirm manual VAT submission</DialogTitle>
            <DialogContent dividers>
              <VuiTypography mb={1}>
                You are about to record this VAT return as manually submitted for period {period}.
              </VuiTypography>
              <VuiBox component="ul" sx={{ pl: 2 }}>
                {ALL_BOXES.map((box) => (
                  <li key={box}>
                    <VuiTypography>
                      {box.toUpperCase()}: {boxes[box]}
                    </VuiTypography>
                  </li>
                ))}
              </VuiBox>
              <VuiTypography mt={1} color="warning">
                This updates the client record in LedgerHub only. It does not send the return to HMRC.
              </VuiTypography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleConfirmSubmit} disabled={submitting}>
                Confirm Manual Submit
              </Button>
            </DialogActions>
          </Dialog>

          {status === "submitted" && result && (
            <VuiBox mt={2} role="status" aria-live="polite" tabIndex={-1} ref={resultRef}>
              <VuiTypography color="success">VAT return recorded successfully.</VuiTypography>
              <VuiTypography>Submission ID: {result.submissionId}</VuiTypography>
              <VuiTypography>Timestamp: {result.timestamp}</VuiTypography>
              <VuiTypography>Status: {result.status}</VuiTypography>
              {result.receipt && <VuiTypography>{result.receipt}</VuiTypography>}
            </VuiBox>
          )}

          {error && (
            <VuiTypography color="error" mt={2} role="alert">
              {error}
            </VuiTypography>
          )}
        </>
      )}
    </VuiBox>
  );
}

export default VATSubmissionForm;
